import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import './SampleSlideViewer.css';

const PDF_URL = '/sample-lecture-slide.pdf';
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export default function SampleSlideViewer() {
  const [pdf, setPdf] = useState(null);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState(false);
  const canvasRef = useRef(null);
  const total = pdf ? pdf.numPages : 0;

  const prev = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIdx(i => Math.min(total - 1, i + 1)), [total]);

  useEffect(() => {
    let active = true;
    const task = pdfjsLib.getDocument(PDF_URL);
    task.promise
      .then(doc => { if (active) setPdf(doc); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; task.destroy(); };
  }, []);

  useEffect(() => {
    if (!pdf) return;
    let renderTask;
    let cancelled = false;
    pdf.getPage(idx + 1).then(page => {
      const canvas = canvasRef.current;
      if (cancelled || !canvas) return;
      const base = page.getViewport({ scale: 1 });
      const scale = (Math.max(canvas.parentElement.clientWidth, 640) / base.width) * (window.devicePixelRatio || 1);
      const viewport = page.getViewport({ scale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport });
      renderTask.promise.catch(() => {});
    });
    return () => { cancelled = true; if (renderTask) renderTask.cancel(); };
  }, [pdf, idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next]);

  return (
    <div className="ssv-wrap">
      <div className="ssv-stage">
        {error
          ? <p className="ssv-pdf-msg">Could not load the sample slides.</p>
          : <canvas ref={canvasRef} className="ssv-pdf" />}
      </div>
      <div className="ssv-controls">
        <button className="ssv-nav-btn" onClick={prev} disabled={idx === 0}>← Prev</button>
        <div className="ssv-dots">
          {Array.from({ length: total }, (_, i) => (
            <button key={i} className={`ssv-dot${i === idx ? ' ssv-dot--active' : ''}`}
              onClick={() => setIdx(i)} title={`Slide ${i + 1}`} />
          ))}
        </div>
        <span className="ssv-counter">{total ? `${idx + 1} / ${total}` : ''}</span>
        <button className="ssv-nav-btn" onClick={next} disabled={!total || idx === total - 1}>Next →</button>
        <a href={PDF_URL} download className="ssv-dl-btn">⬇ Download PDF</a>
      </div>
    </div>
  );
}
